import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowUpRight } from 'lucide-react';
import { FundleLogo } from './FundleLogo';

// Editorial Brain-centric navigation (Oct 2026) — modelled on the reference
// fundle-brain-rebuild site. Replaces the Zoho-clone nav. Clean white bar,
// navy links, deep-blue CTA "Meet Fundle Brain" with up-right arrow.

const NAV = [
  { label: 'Fundle Brain', href: '/products/brain' },
  {
    label: 'For retail',
    href: '/retail-ai',
    menu: [
      { name: 'Overview', desc: 'The AI engine for retail brands', href: '/retail-ai' },
      { name: 'Fundle Loyalty', desc: 'Customer growth & retention', href: '/products/loyalty' },
      { name: 'Campaign intelligence', desc: 'The right offer, every time', href: '/products/brain' },
      { name: 'Marketplace reconciliation', desc: 'Find the money that didn\'t arrive', href: '/products/adsr' },
      { name: 'MBO & SIS', desc: 'Distributed retail operations', href: '/solutions/brands' },
      { name: 'All retail workflows', desc: 'Browse every scenario', href: '/solutions' },
    ],
  },
  {
    label: 'For malls',
    href: '/mall-ai',
    menu: [
      { name: 'Overview', desc: 'The AI engine for shopping malls', href: '/mall-ai' },
      { name: 'ADSR & tenant sales', desc: 'Know the trading day', href: '/products/adsr' },
      { name: 'Mall loyalty', desc: 'From visitor to repeat shopper', href: '/products/loyalty' },
      { name: 'AI concierge', desc: 'The visitor morning brief', href: '/products/brain' },
      { name: 'Retail media', desc: 'Monetise what the mall sees', href: '/products/reach' },
      { name: 'All mall workflows', desc: 'Browse every scenario', href: '/solutions/malls' },
    ],
  },
  {
    label: 'Explore',
    href: '/resources',
    menu: [
      { name: 'Agent stories', desc: 'How Brain reasons in real contexts', href: '/customers' },
      { name: 'Guides', desc: 'Longer-form explanations', href: '/resources' },
      { name: 'Industries', desc: 'Retail categories & rhythms', href: '/solutions' },
      { name: 'About & leadership', desc: 'Who builds Fundle', href: '/about' },
      { name: 'Pricing', desc: 'Scope-based commercial model', href: '/pricing' },
      { name: 'Contact', desc: 'Reach the team', href: '/contact' },
    ],
  },
];

const NavigationZoho = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  return (
    <header
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100"
      data-testid="nav-root"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" className="flex-shrink-0" data-testid="nav-logo">
            <FundleLogo />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.menu && setOpenMenu(item.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <Link
                  to={item.href}
                  className="inline-flex items-center gap-1 text-[#14223D] hover:text-[#2563EB] font-medium text-[14.5px] transition-colors"
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                  {item.menu && <ChevronDown size={13} strokeWidth={2.4} className="text-slate-400" />}
                </Link>

                {item.menu && openMenu === item.label && (
                  <div
                    className="absolute right-0 top-full pt-3 w-[460px]"
                    data-testid={`nav-mega-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="bg-white border border-slate-200 rounded-lg shadow-xl p-3">
                      {item.menu.map((m) => (
                        <Link
                          key={m.href}
                          to={m.href}
                          className="group flex items-center justify-between p-3 rounded-md hover:bg-blue-50 transition-colors"
                          data-testid={`nav-mega-item-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        >
                          <div className="min-w-0 pr-3">
                            <p className="text-[#14223D] font-semibold text-[14px] group-hover:text-[#2563EB]">{m.name}</p>
                            <p className="text-slate-500 text-[12.5px] leading-snug mt-0.5">{m.desc}</p>
                          </div>
                          <ArrowUpRight size={14} className="text-slate-300 group-hover:text-[#2563EB] flex-shrink-0" strokeWidth={2.2} />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center">
            <Link
              to="/book-demo"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#2563EB] text-white font-semibold text-[13.5px] rounded-md hover:bg-[#1D4ED8] transition-colors shadow-sm"
              data-testid="nav-book-demo"
            >
              Meet Fundle Brain <ArrowUpRight size={13} strokeWidth={2.6} />
            </Link>
          </div>

          <button
            type="button"
            className="lg:hidden text-[#14223D] p-2 -mr-2"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-100 bg-white max-h-[calc(100vh-72px)] overflow-y-auto" data-testid="nav-mobile-drawer">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {NAV.map((item) => (
              <div key={item.label}>
                <Link to={item.href} className="block py-2.5 text-[#14223D] font-semibold text-[15px]">
                  {item.label}
                </Link>
                {item.menu && (
                  <div className="pl-4 pb-2 space-y-1">
                    {item.menu.map((m) => (
                      <Link key={m.href} to={m.href} className="block py-1.5 text-slate-600 text-[13.5px] hover:text-[#2563EB]">
                        {m.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-3 border-t border-slate-100">
              <Link
                to="/book-demo"
                className="inline-flex items-center justify-center w-full gap-1.5 px-5 py-3 bg-[#2563EB] text-white font-semibold text-[14px] rounded-md"
                data-testid="nav-mobile-book-demo"
              >
                Meet Fundle Brain <ArrowUpRight size={14} strokeWidth={2.6} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default NavigationZoho;
